import { useQuery } from "@apollo/client/react";
import ModalProvider from "@repo/components/src/globalProviders/modal";
import { Badge, BadgeIcon, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { Card } from "@repo/components/src/ui/card";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { SkeletonText } from "@repo/components/src/ui/skeleton";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { UserReviewDocument } from "@repo/graphql/documents/review/query.generated";
import { WorkOrdersDocument } from "@repo/graphql/documents/workOrder/query.generated";
import { MontuWorkOrderStatusChoices } from "@repo/graphql/gql/graphql";
import { Link } from "expo-router";
import { StarIcon } from "lucide-react-native";
import React, { ComponentProps } from "react";

type Props = {
  userId: string;
  brandId: string;
};

const SharedBrandActivity = React.memo((props: Props) => {
  const { userId, brandId } = props;
  const workOrdersQuery = useQuery(WorkOrdersDocument, {
    variables: {
      ambassador_Id: userId,
      campaign_Brand_Id: brandId,
      orderBy: "-id",
      first: 10,
      status_In: [
        MontuWorkOrderStatusChoices.Accepted,
        MontuWorkOrderStatusChoices.Finished,
        MontuWorkOrderStatusChoices.Declined,
      ],
    },
  });
  const reviewsQuery = useQuery(UserReviewDocument, {
    variables: { userId: userId, brandId: brandId },
  });
  const isLoading = workOrdersQuery.loading || reviewsQuery.loading;
  return (
    <HStack flexSpace="md">
      <Box className="w-full">
        <Card variant="outline" size="lg">
          <Heading bold>Actividad con tu marca</Heading>
          <Text className="mt-2">
            Historial de campañas, contenidos y desempeño de este creador
            contigo.
          </Text>
          <Heading size="sm" bold className="mt-6">
            Últimas campañas
          </Heading>
          <Box className="mt-4">
            <SkeletonText
              _lines={6}
              className="h-8 rounded-lg"
              isLoaded={!isLoading}
            >
              <VStack space="md">
                {(workOrdersQuery.data?.workOrder.list.edges.length || 0) >
                0 ? (
                  workOrdersQuery.data?.workOrder.list.edges.map((edge) => {
                    const campaign = edge.node.campaign;
                    const workOrder = edge.node;
                    const brand = edge.node.campaign.brand;
                    const action: ComponentProps<typeof Badge>["action"] =
                      workOrder.status.enum ===
                      MontuWorkOrderStatusChoices.Accepted
                        ? "muted"
                        : workOrder.status.enum ===
                            MontuWorkOrderStatusChoices.Finished
                          ? "success"
                          : workOrder.status.enum ===
                              MontuWorkOrderStatusChoices.Declined
                            ? "error"
                            : "error";
                    return (
                      <HStack
                        key={workOrder.id}
                        space="md"
                        className="items-center"
                      >
                        <Link
                          className="flex-1 shrink hover:underline"
                          onPress={ModalProvider.hide}
                          href={{
                            pathname:
                              "/app/(brands)/[brandId]/(main)/campaign/[campaignId]",
                            params: {
                              brandId: brandId,
                              campaignId: campaign.id,
                            },
                          }}
                        >
                          <Text numberOfLines={1}>
                            {campaign.title} - {brand.name}
                          </Text>
                        </Link>
                        <Badge action={action} size="md" rounded="lg">
                          <BadgeText>{workOrder.status.display}</BadgeText>
                        </Badge>
                      </HStack>
                    );
                  })
                ) : (
                  <Text>No has trabajado con este creador.</Text>
                )}
              </VStack>
            </SkeletonText>
          </Box>
        </Card>
      </Box>
      <Box className="w-full">
        <Card variant="outline" size="lg">
          <Heading bold>Contenidos para tu marca</Heading>
          <HStack flexSpace="md" className="mt-3">
            {(reviewsQuery.data?.review.userReview.lastReviews.length || 0) >
            0 ? (
              reviewsQuery.data?.review.userReview.lastReviews.map((review) => {
                return (
                  <Box key={review.id} className="w-1/3">
                    <Box className="p-4 shadow-md rounded-lg h-full">
                      <HStack space="md">
                        <Heading
                          className="flex-1 shrink font-semibold"
                          numberOfLines={1}
                          size="sm"
                        >
                          Review
                        </Heading>
                        <Badge
                          action="muted"
                          className="border gap-x-1 items-center"
                          size="sm"
                        >
                          <BadgeText>{review.calification} de 5</BadgeText>
                          <BadgeIcon as={StarIcon} />
                        </Badge>
                      </HStack>
                      <Text className="mt-2 flex-1" numberOfLines={4}>
                        {review.comment}
                      </Text>
                      <Heading
                        className="font-semibold mt-6"
                        numberOfLines={1}
                        size="sm"
                      >
                        Campaña
                      </Heading>
                      <Text className="mt-2" numberOfLines={4}>
                        {review.name} - {review.reviewer}
                      </Text>
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Text className="mt-2">No tienes reviews para este creador.</Text>
            )}
          </HStack>
        </Card>
      </Box>
    </HStack>
  );
});

SharedBrandActivity.displayName = "SharedBrandActivity";

export default SharedBrandActivity;
